import { useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";

export interface FileUploadProps {
  onFilesSelected: (files: FileList) => void | Promise<void>;
  isUploading?: boolean;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function FileUpload({
  onFilesSelected,
  isUploading = false,
  accept = ".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp",
  multiple = true,
  disabled = false,
  className = "",
  children,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFilesSelected]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!disabled && !isUploading) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, isUploading, handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFiles(e.target.files)}
        multiple={multiple}
        accept={accept}
        className="hidden"
        disabled={disabled || isUploading}
      />
      <div
        className={`
          bg-[#f0f9ff] rounded-lg border-2 border-dashed p-5 transition-colors cursor-pointer
          ${isDragOver ? "border-primary bg-primary/5" : "border-[#7dd3fc] hover:border-primary"}
          ${disabled || isUploading ? "opacity-50 cursor-not-allowed" : ""}
          ${className}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        {children || <DefaultUploadContent isUploading={isUploading} />}
      </div>
    </>
  );
}

function DefaultUploadContent({ isUploading }: { isUploading: boolean }) {
  return (
    <>
      <h3 className="text-[15px] font-bold text-foreground mb-3">Upload Materials</h3>
      <div className="flex flex-col items-center py-2">
        <div className="flex items-end gap-0 mb-3">
          <div className="relative">
            <svg
              className="h-12 w-12 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            <svg
              className="absolute -top-1 right-0 h-3.5 w-3.5 text-primary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </div>
          <div className="relative -ml-2">
            <svg
              className="h-9 w-9 text-primary/70"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            <svg
              className="absolute -top-1 right-0 h-2.5 w-2.5 text-primary/70"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </div>
        </div>
        <Button
          type="button"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium gap-2 px-4 h-9 text-[13px]"
          disabled={isUploading}
          onClick={(e) => e.stopPropagation()}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Upload Files
            </>
          )}
        </Button>
      </div>
      <p className="text-center text-[11px] text-muted-foreground leading-relaxed mt-2">
        Drag and drop files here or click to upload.
        <br />
        PDFs, Docs, Images, PowerPoint, and more...
      </p>
    </>
  );
}

export default FileUpload;
